import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      id: 'introduction',
      title: '1. Introduction',
      icon: 'FileText',
      content: `Welcome to Sri Dhanvantari Ayurvaidik Parishkar Kendra ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website ayurparishkarkendra.com, use our mobile application "Ayur Parishkar Kendra WA", and interact with our services through Meta Platforms (WhatsApp Business Cloud API and Facebook Login) and Razorpay Payment Gateway.

This policy complies with:
• Meta Platform Policies and WhatsApp Business Messaging Policies
• Razorpay Privacy and Data Protection Policies
• Information Technology Act, 2000 and Digital Personal Data Protection Act, 2023 (India)
• Applicable Indian laws and regulations

By accessing or using our website, app, and services, you agree to the terms of this Privacy Policy. If you do not agree with our policies and practices, please do not use our services.`
    },
    {
      id: 'platform-compliance',
      title: '1.5 Platform-Specific Compliance',
      icon: 'Shield',
      content: `**Meta Platforms (WhatsApp Business & Facebook) Compliance:**

Our app "Ayur Parishkar Kendra WA" uses WhatsApp Business Cloud API and Facebook Login provided by Meta Platforms, Inc. We comply with:
• Meta Platform Policies
• WhatsApp Business Messaging Policies
• Facebook Platform Terms

**What We Use WhatsApp For:**
• Appointment booking and confirmations
• Health consultation and customer support
• Service updates and health tips (with your consent)
• Responding to your inquiries

**What We Use Facebook Login For:**
• Simplified account creation and authentication
• Pre-filling your profile information (name, email) with your permission
• We only access basic profile information you explicitly authorize

**Your WhatsApp Privacy:**
• We only receive messages you send to our business number
• We do NOT access your WhatsApp contact list
• We do NOT read messages sent to other contacts
• WhatsApp messages are end-to-end encrypted by Meta
• You can opt-out of WhatsApp communications anytime by messaging "STOP"

**Razorpay Payment Gateway Compliance:**

We use Razorpay for secure payment processing. Razorpay is:
• PCI-DSS Level 1 certified (highest security standard)
• Compliant with RBI (Reserve Bank of India) guidelines
• Registered payment aggregator in India

**Payment Security Guarantees:**
• We NEVER store your complete card numbers
• We NEVER store your CVV or PIN
• We NEVER store your bank account passwords
• We NEVER store your UPI PIN
• All payment credentials are handled exclusively by Razorpay's secure infrastructure

**What We Collect for Payments:**
• Customer name, phone, and email (for invoicing)
• Transaction ID and payment status
• Amount paid and payment method type (UPI, card, etc.)
• Last 4 digits of card (for reference only, provided by Razorpay)

**Data Location:**
• Your data is stored in secure cloud infrastructure within India or with appropriate safeguards
• Payment data is processed by Razorpay in compliance with Indian regulations
• WhatsApp messages are transmitted through Meta's global infrastructure with encryption`
    },
    {
      id: 'information-collection',
      title: '2. Information We Collect',
      icon: 'Database',
      content: `We collect several types of information from and about users of our website and services:

**Personal Information:**
• Name, email address, phone number, and postal address
• Date of birth and gender
• Medical history and health information (for treatment purposes)
• Payment and billing information
• Government-issued identification (for verification purposes)

**Information Collected via Meta Platforms (WhatsApp Business Cloud API & Facebook Login):**
• Name (as provided in your WhatsApp/Facebook profile)
• Phone number (for WhatsApp communication)
• City-level location (only if you explicitly permit)
• WhatsApp message content (appointment requests, health queries, service communications)
• Message metadata (timestamps, delivery status, read receipts)
• Facebook user ID (if you use Facebook Login)

**IMPORTANT - What We DO NOT Collect:**
• We do NOT track your precise GPS location or real-time location
• We do NOT access your WhatsApp contact list
• We do NOT read messages sent to other contacts
• We do NOT collect data from your Facebook friends or connections

**Information Collected via Razorpay Payment Gateway:**
• Customer name, phone number, and email address
• Transaction ID and payment reference number
• Payment status and amount paid
• Payment method used (UPI, card type, net banking, wallet, etc.)
• Billing address (if provided)

**IMPORTANT - Payment Security:**
• We do NOT store your card numbers, CVV, or PIN
• We do NOT store your bank account credentials
• We do NOT store your UPI PIN or passwords
• All sensitive payment data is handled exclusively by Razorpay (PCI-DSS Level 1 certified)

**Automatically Collected Information:**
• IP address, browser type, and operating system
• Pages visited, time spent on pages, and navigation patterns
• Device information and unique device identifiers
• Cookies and similar tracking technologies

**Information from Third Parties:**
• Social media profile information (if you connect via social login)
• Payment processor information (Razorpay)
• Analytics providers (Google Analytics)`
    },
    {
      id: 'how-we-use',
      title: '3. How We Use Your Information',
      icon: 'Settings',
      content: `We use the information we collect for the following purposes:

**Service Delivery:**
• To provide, maintain, and improve our Ayurvedic treatment services
• To process appointments and consultations
• To communicate with you about your treatments and appointments
• To provide customer support and respond to inquiries

**WhatsApp Business Communication (via Meta Platforms):**
• To send appointment confirmations and reminders
• To provide health tips and treatment updates
• To respond to your health-related queries
• To share service information and updates
• To facilitate customer support via WhatsApp messaging
• All WhatsApp communications are opt-in and you can opt-out anytime by messaging "STOP"

**Facebook Login Integration:**
• To simplify your account creation and login process
• To verify your identity using Facebook authentication
• To pre-fill your profile information (name, email) with your consent
• We only access basic profile information you explicitly authorize

**Payment Processing (via Razorpay):**
• To process payments securely for appointments and services
• To generate invoices and receipts
• To prevent fraud and ensure secure transactions
• To maintain billing records for accounting and tax compliance
• To process refunds when applicable

**Marketing and Communications (with your explicit consent):**
• To send promotional materials and special offers
• To conduct surveys and gather feedback
• To personalize your experience on our website
• You can opt-out of marketing communications at any time

**Legal and Compliance:**
• To comply with legal obligations under Indian law (IT Act, DPDP Act)
• To comply with healthcare regulations and medical record-keeping requirements
• To protect our rights and prevent fraud
• To enforce our terms and conditions
• To respond to legal requests from authorities`
    },
    {
      id: 'information-sharing',
      title: '4. Information Sharing and Disclosure',
      icon: 'Share2',
      content: `We may share your information with:

**Meta Platforms, Inc. (WhatsApp Business Cloud API & Facebook):**
• Message content is transmitted through Meta's WhatsApp Business Cloud API for delivery
• Facebook user ID and basic profile data (if you use Facebook Login)
• Meta processes this data according to their own privacy policy and platform terms
• We use Meta's services solely for communication and authentication purposes

**Razorpay Payment Gateway:**
• Customer name, phone number, email, and transaction details
• Razorpay processes payments according to their privacy policy and PCI-DSS standards
• Payment data is encrypted and securely transmitted
• We do NOT share your complete card details or banking credentials with anyone

**Other Service Providers:**
• Cloud storage providers (Supabase) for secure data hosting
• Email service providers for appointment confirmations
• SMS service providers for notifications
• Analytics providers (Google Analytics) for website usage analysis

**Security Measures for Data Sharing:**
• All data transmission uses HTTPS encryption
• Third-party providers are contractually bound to protect your data
• We share only the minimum necessary information
• Access is restricted to authorized personnel only

**Legal Requirements:**
• When required by law, court order, or government regulation
• To protect our rights, property, or safety
• To prevent fraud or illegal activities
• To comply with tax and accounting regulations

**Business Transfers:**
• In connection with a merger, acquisition, or sale of assets
• You will be notified of any such change

**With Your Consent:**
• When you explicitly authorize us to share your information

**IMPORTANT - What We DO NOT Do:**
• We do NOT sell your personal information to third parties
• We do NOT rent your data to marketers or advertisers
• We do NOT share your medical records without your explicit consent (except as required by law)
• We do NOT use your data for purposes other than those stated in this policy`
    },
    {
      id: 'data-security',
      title: '5. Data Security',
      icon: 'Lock',
      content: `We implement appropriate technical and organizational security measures to protect your personal information:

**Technical Security Measures:**
• HTTPS/SSL/TLS encryption for all data transmission
• End-to-end encryption for WhatsApp messages (provided by Meta)
• Secure payment processing through PCI-DSS Level 1 compliant Razorpay
• Encrypted data storage in secure cloud infrastructure (Supabase)
• Regular security audits and vulnerability assessments
• Multi-factor authentication for administrative access
• Firewall protection and intrusion detection systems
• Regular backups and disaster recovery procedures

**Platform-Level Security:**
• Meta Platforms provides enterprise-grade security for WhatsApp Business API
• Razorpay maintains PCI-DSS compliance for payment data
• All third-party services are vetted for security standards

**Organizational Security Measures:**
• All staff members are trained on data privacy and security
• Access to personal information is restricted on a need-to-know basis
• Confidentiality agreements are in place with all employees
• Regular privacy and security training for staff
• Incident response procedures for data breaches

**Your Role in Security:**
• Keep your login credentials confidential
• Use strong passwords and enable two-factor authentication
• Do not share sensitive information via unsecured channels
• Report any suspicious activity immediately

While we strive to protect your information using industry-standard security measures, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security but are committed to protecting your data to the best of our ability.`
    },
    {
      id: 'cookies',
      title: '6. Cookies and Tracking Technologies',
      icon: 'Cookie',
      content: `We use cookies and similar tracking technologies to enhance your experience:

**Types of Cookies:**
• **Essential Cookies:** Required for website functionality
• **Analytics Cookies:** Help us understand how visitors use our site (Google Analytics)
• **Marketing Cookies:** Used for targeted advertising (Facebook Pixel)
• **Preference Cookies:** Remember your settings and preferences

**Third-Party Cookies:**
• Google Analytics for website analytics
• Facebook Pixel for advertising and remarketing
• Razorpay for payment processing

**Your Choices:**
You can control cookies through your browser settings. However, disabling cookies may affect website functionality.`
    },
    {
      id: 'your-rights',
      title: '7. Your Privacy Rights',
      icon: 'UserCheck',
      content: `You have the following rights regarding your personal information:

**Access and Portability:**
• Request a copy of your personal information
• Receive your data in a structured, machine-readable format

**Correction and Update:**
• Request correction of inaccurate or incomplete information
• Update your account information at any time

**Deletion:**
• Request deletion of your personal information (subject to legal requirements)
• Close your account and remove your data
• If you signed up via Facebook, you can request data deletion through Facebook's settings

**Facebook Data Deletion:**
If you used Facebook Login, you can request deletion of your data in two ways:
1. Through Facebook: Go to your Facebook Settings → Apps and Websites → Select our app → Remove
2. Direct Request: Visit our data deletion status page to track your request
When you request deletion through Facebook, we will permanently delete all your data within 30 days, including:
• Your account and profile information
• All appointments and medical records
• Contact information and communications
• Any other personal data associated with your account

**Opt-Out:**
• Unsubscribe from marketing communications
• Opt-out of targeted advertising
• Disable cookies through browser settings

**Restriction:**
• Request restriction of processing in certain circumstances
• Object to processing based on legitimate interests

To exercise these rights, please contact us at ayurparishkarkendra@gmail.com or call +91 78422 02255.`
    },
    {
      id: 'children-privacy',
      title: '8. Children\'s Privacy',
      icon: 'Baby',
      content: `Our services are not directed to children under 13 years of age. We do not knowingly collect personal information from children under 13.

If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately. We will take steps to remove such information from our systems.

For users between 13-18 years of age, we require parental consent before collecting or processing personal information.`
    },
    {
      id: 'international-transfers',
      title: '9. International Data Transfers',
      icon: 'Globe',
      content: `Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws.

We ensure appropriate safeguards are in place when transferring your information internationally:
• Standard contractual clauses approved by regulatory authorities
• Adequacy decisions by relevant data protection authorities
• Your explicit consent when required

By using our services, you consent to the transfer of your information as described in this policy.`
    },
    {
      id: 'retention',
      title: '10. Data Retention',
      icon: 'Archive',
      content: `We retain your personal information only as long as necessary to fulfill the purposes outlined in this Privacy Policy and as required by Indian law:

**Active Accounts:**
• Information is retained while your account is active
• Medical records are retained as required by healthcare regulations (minimum 10 years)
• WhatsApp message history is retained for customer service purposes

**Inactive Accounts:**
• Account information may be retained for up to 7 years after last activity
• Medical records are retained for 10 years as per Indian medical regulations
• Payment records are retained for 7 years for tax and audit compliance

**WhatsApp Communication Data:**
• Message content is retained for up to 2 years or earlier if no longer required for service delivery, legal, or compliance purposes
• Message metadata (timestamps, delivery status) may be retained longer for analytics and service improvement
• You can request deletion of your WhatsApp message history at any time

**Payment Data (Razorpay):**
• Transaction records are retained for 7 years as required by Indian tax laws
• Payment method details (card type, last 4 digits) are retained for reference
• Complete card numbers and CVV are NEVER stored by us

**Legal Requirements:**
• Information may be retained longer if required by law or legal proceedings
• Financial records are retained for tax and audit purposes as per Indian regulations
• Medical records follow healthcare industry retention standards

**Data Deletion:**
You can request deletion of your information at any time by contacting us at ayurparishkarkendra@gmail.com. However, we may retain certain information:
• As required by law (medical records, financial records)
• To resolve disputes or enforce our agreements
• For legitimate business purposes (fraud prevention, security)

When you delete your account or request data deletion, we will:
• Permanently delete your personal information within 30 days
• Anonymize data that must be retained for legal purposes
• Notify you when the deletion is complete`
    },
    {
      id: 'third-party-links',
      title: '11. Third-Party Links and Services',
      icon: 'Link',
      content: `Our website and app integrate with third-party services. Each has its own privacy policy:

**Meta Platforms, Inc.:**
• WhatsApp Business Cloud API - Privacy Policy: https://www.whatsapp.com/legal/business-policy
• Facebook Login - Privacy Policy: https://www.facebook.com/privacy/policy
• Meta processes data according to their own privacy policies
• We recommend reviewing Meta's privacy policies to understand how they handle your data

**Razorpay Payment Gateway:**
• Privacy Policy: https://razorpay.com/privacy/
• Razorpay is PCI-DSS Level 1 certified
• Payment data is processed according to Razorpay's security standards
• We recommend reviewing Razorpay's privacy policy for payment data handling

**Other Third-Party Services:**
• Google Maps for location services
• Google Analytics for website analytics
• Social media platforms (Instagram, LinkedIn) for social sharing
• Supabase for cloud data storage

**Important Notice:**
• We are not responsible for the privacy practices of third-party services
• Each third-party service has its own privacy policy and terms of service
• We encourage you to review their privacy policies before using these services
• This Privacy Policy applies only to information collected directly by us

**Third-Party Links:**
Our website may contain links to external websites. We are not responsible for the content or privacy practices of these external sites.`
    },
    {
      id: 'updates',
      title: '12. Changes to This Privacy Policy',
      icon: 'RefreshCw',
      content: `We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements.

**Notification of Changes:**
• We will post the updated policy on this page
• The "Last Updated" date will be revised
• For material changes, we will provide prominent notice on our website
• We may also notify you via email

**Your Continued Use:**
Your continued use of our services after changes are posted constitutes acceptance of the updated Privacy Policy.

We encourage you to review this policy periodically to stay informed about how we protect your information.`
    },
    {
      id: 'contact',
      title: '13. Contact Us',
      icon: 'Mail',
      content: `If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:

**Sri Dhanvantari Ayurvaidik Parishkar Kendra**

📍 **Address:**
1st floor, G94C+WWP BLOCK-1, LAKSHMI NARAYANA ESTATES
24, High Tension Line Rd, Nadigada Tanda
Miyapur, Hyderabad, Telangana 500049

📞 **Phone:**
+91 78422 07799 / +91 78422 02255

📧 **Email:**
ayurparishkarkendra@gmail.com

🌐 **Website:**
https://ayurparishkarkendra.com

⏰ **Business Hours:**
Tuesday - Sunday: 8:00 AM - 7:00 PM
Break: 1:00 PM - 3:00 PM
Monday: 10:00 AM - 6:00 PM

We will respond to your inquiry within 48 hours.`
    },
    {
      id: 'consent',
      title: '14. Consent',
      icon: 'CheckCircle',
      content: `By using our website and services, you consent to:

• The collection and use of your information as described in this Privacy Policy
• The use of cookies and tracking technologies
• The processing of your health information for treatment purposes
• Communication from us regarding your appointments and services
• The transfer of your information to third-party service providers

You can withdraw your consent at any time by:
• Contacting us at ayurparishkarkendra@gmail.com
• Updating your account preferences
• Unsubscribing from marketing communications

Withdrawal of consent may affect our ability to provide certain services to you.`
    }
  ];

  return (
    <>
      <Helmet>
        <title>Privacy Policy - Sri Dhanvantari Ayurvaidik Parishkar Kendra</title>
        <meta
          name="description"
          content="Privacy Policy for Sri Dhanvantari Ayurvaidik Parishkar Kendra. Learn how we collect, use, and protect your personal information."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://ayurparishkarkendra.com/privacy-policy" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-24 pb-16">
          {/* Hero Section */}
          <div className="bg-gradient-to-br from-primary/10 via-accent/5 to-background py-12 mb-12">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-center mb-4">
                <Icon name="Shield" size={48} className="text-primary" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-center text-foreground mb-4">
                Privacy Policy
              </h1>
              <p className="text-center text-muted-foreground text-lg max-w-2xl mx-auto">
                Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.
              </p>
              <p className="text-center text-sm text-muted-foreground mt-4">
                Last Updated: January 3, 2026
              </p>
            </div>
          </div>

          {/* Content Sections */}
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {sections.map((section, index) => (
              <div
                key={section.id}
                id={section.id}
                className="mb-12 scroll-mt-24"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name={section.icon} size={24} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-foreground mb-4">
                      {section.title}
                    </h2>
                    <div className="prose prose-lg max-w-none text-muted-foreground whitespace-pre-line">
                      {section.content}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default PrivacyPolicy;

